import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PublicContainer } from '@/components/layouts/PublicContainer';
import { useJsonLd } from '@/hooks/useJsonLd';
import { usePageSEO } from '@/hooks/usePageSEO';
import { pageMetadata } from '@/lib/seo/metadata';
import { SITE_URL, type PublicRoute } from '@/lib/seo/public-routes';
import { breadcrumbJsonLd } from '@/lib/seo/structured-data';
import { CLI_DOCS_URL } from '@/lib/contact';
import { useCtaTracker } from '@/hooks/useCtaTracker';

/**
 * The shell the guide pages share.
 *
 * It owns the contracts those pages have to meet — registry-driven metadata, a
 * breadcrumb whose structured data mirrors what is on screen, and the shared
 * content width — so a new guide cannot quietly skip one. It deliberately
 * renders no `main`, `header` or `footer`: PublicLayout owns those landmarks.
 */
export function GuideLayout({
  route,
  eyebrow,
  title,
  intro,
  children,
}: {
  route: PublicRoute;
  eyebrow: string;
  title: string;
  intro: string;
  children: React.ReactNode;
}) {
  usePageSEO(pageMetadata(route));

  // Mirrors the trail rendered below, 1:1.
  const breadcrumb = useMemo(
    () => breadcrumbJsonLd([{ name: 'Home', url: `${SITE_URL}/` }, { name: title }]),
    [title],
  );
  useJsonLd('guide-breadcrumb', breadcrumb);

  return (
    <div className="bg-background py-16 sm:py-24">
      <PublicContainer>
        <div className="mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mono text-[11px] uppercase tracking-wider text-muted-foreground"
          >
            <ol className="flex flex-wrap items-center gap-x-2">
              <li>
                <Link to="/" className="transition-colors hover:text-foreground">Home</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-foreground">{title}</li>
            </ol>
          </nav>

          <p className="mono mt-8 text-[11px] uppercase tracking-wider text-lime">{eyebrow}</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {title}
          </h1>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">{intro}</p>

          <div className="mt-12 flex flex-col gap-12">{children}</div>
        </div>
      </PublicContainer>
    </div>
  );
}

/** One section of a guide. Headings stay in order so the outline is navigable. */
export function GuideSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">{title}</h2>
      {children}
    </section>
  );
}

export function GuideParagraph({ children }: { children: React.ReactNode }) {
  return <p className="text-[15px] leading-relaxed text-muted-foreground">{children}</p>;
}

/** A list where each item leads with the thing being named. */
export function GuideList({ items }: { items: { term: string; detail: string }[] }) {
  return (
    <ul className="flex flex-col gap-3 border-l border-hairline pl-5">
      {items.map((item) => (
        <li key={item.term} className="text-[15px] leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">{item.term}</span> — {item.detail}
        </li>
      ))}
    </ul>
  );
}

/** A command or config sample. Scrolls inside itself rather than widening the page. */
export function GuideCode({ children, label }: { children: string; label: string }) {
  return (
    <figure className="m-0">
      <figcaption className="mono mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </figcaption>
      <pre className="mono overflow-x-auto rounded-lg border border-hairline bg-surface-1 p-4 text-[12.5px] leading-relaxed text-foreground/90">
        {children}
      </pre>
    </figure>
  );
}

/** Closes a guide with the conversion path the record specifies. */
export function GuideCta({ children }: { children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-hairline bg-surface-1 p-6 md:p-8">
      <h2 className="text-xl font-semibold tracking-tight text-foreground">Try it on your own code</h2>
      {children}
    </section>
  );
}

/**
 * The action row every guide closes with.
 *
 * The guides share one CTA hierarchy — signup is the primary, the CLI and the
 * product docs are the alternatives for someone not ready for an account — so
 * they share one implementation. That keeps the analytics contract in a single
 * place: a new guide gets the same `guide_cta` placement and the same closed
 * set of actions without having to remember to wire anything.
 */
export function GuideCtaActions({
  cliHref = CLI_DOCS_URL,
  includeBlog = false,
}: {
  /** Overridden by guides that link a specific CLI page rather than its home. */
  cliHref?: string;
  includeBlog?: boolean;
}) {
  const trackCta = useCtaTracker();
  const secondaryClassName =
    'text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground';

  return (
    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
      <Link
        to="/signup"
        className="inline-flex items-center justify-center rounded-md bg-lime px-4 py-2.5 text-sm font-medium text-lime-foreground transition-all hover:brightness-105 active:scale-[0.98]"
        onClick={() => trackCta({ action: 'signup', placement: 'guide_cta', priority: 'primary' })}
      >
        Connect a repository
      </Link>
      <a
        href={cliHref}
        target="_blank"
        rel="noreferrer"
        className={secondaryClassName}
        onClick={() => trackCta({ action: 'cli_docs', placement: 'guide_cta', priority: 'secondary' })}
      >
        Read the CLI documentation
      </a>
      <Link
        to="/docs"
        className={secondaryClassName}
        onClick={() => trackCta({ action: 'product_docs', placement: 'guide_cta', priority: 'secondary' })}
      >
        Product docs
      </Link>
      {/* Not a funnel destination, so it carries no event. */}
      {includeBlog && <Link to="/blog" className={secondaryClassName}>Blog</Link>}
    </div>
  );
}

/**
 * A product screen, rendered from the SVG the CLI documentation ships.
 *
 * The source assets live in `debtdrone-cli/src/assets/screens/` and are copied
 * into `public/product/`; update both together. They carry their own `<title>`
 * and `role="img"`, so the alt text here names what the screen shows rather
 * than repeating the caption.
 */
export function GuideScreenshot({
  src,
  alt,
  caption,
  width,
  height,
}: {
  src: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
}) {
  return (
    <figure className="m-0">
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
        className="w-full rounded-lg border border-hairline bg-surface-1"
      />
      <figcaption className="mono mt-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {caption}
      </figcaption>
    </figure>
  );
}
